/*
 * legacy.js — Single IIFE entry point for the entire legacy platform stack.
 *
 * Consolidates what was previously 15+ separate esbuild entry points:
 *   app_bundle, protenders_legacy, map, charts, reports, notifications,
 *   tasks, company_dashboards, grid_list_toggler, vendor_registrations,
 *   vendor/documents, vendor/registrations/show, projects_pdf_js,
 *   product_requests/requirements/developer_grid,
 *   product_requests/requirements/supplier_grid,
 *   product_requests/suppliers_info_grid
 *
 * Every module is globally available on all legacy pages.
 * The NG stack (ng/application.js) remains fully separate.
 */

// ─────────────────────────────────────────────────────────────────────────────
// PART 1: app_bundle (core platform modules)
// ─────────────────────────────────────────────────────────────────────────────

// ─── Node.js global shim (MUST be first import) ──────────────────────────────
// autocomplete.js → immediate → references `global`. Shim for browser.
import './utils/global-shim.js';

// ─── I18n polyfill ───────────────────────────────────────────────────────────
// Provides window.I18n.t() shim so modules using I18n.t() in property
// values don't crash at load time. Real translations override later.
import './utils/i18n-polyfill.js';

// ─── Vendor Dependencies ─────────────────────────────────────────────────────
// jQuery (expose globally first)
import './utils/jquery-global.js';

// jQuery UI
import 'jquery-ui-dist/jquery-ui.min';

// jQuery Cookie plugin — used by index_snapshot_saver and ag_grid_column_filters
import 'jquery.cookie';

// Vendor globals (agGrid, algoliasearch, instantsearch, moment, _, URI, etc.)
// MUST be a separate import so esbuild processes assignments as module code.
import './utils/vendor-globals.js';

// AG Grid v35 ESM requires explicit module registration.
import { ModuleRegistry, AllEnterpriseModule } from 'ag-grid-enterprise';
ModuleRegistry.registerModules([AllEnterpriseModule]);

// resizeAgGridTable — used by search modules across both layouts
import $ from 'jquery';
window.resizeAgGridTable = function (selector, gridOptions, subHeadersCount, rowsCount, maxRows, offset, inside_tab) {
    if (subHeadersCount == null) { subHeadersCount = 0; }
    if (rowsCount == null) { rowsCount = false; }
    if (maxRows == null) { maxRows = false; }
    if (offset == null) { offset = 2; }
    if (inside_tab == null) { inside_tab = false; }
    var rows = rowsCount || gridOptions.api.getDisplayedRowCount();
    if (maxRows) { rows = rows > maxRows ? maxRows : rows; }
    var height = (gridOptions.rowHeight * rows) + offset + ((1 + subHeadersCount) * gridOptions.headerHeight);
    if (inside_tab) {
        $('.tabs-content .content.active').append($('<div class="tab-content-mock"></div>'));
        var tab_height = $('.tab-content-mock').height();
        $('.tab-content-mock').remove();
        if (height > tab_height) { height = tab_height; }
    }
    return $(selector).css('height', height);
};

// Auto-init dispatcher — DOM-driven module initialization
window._searchAutoInit = function (elementId, moduleName, methodName) {
    function init() {
        var el = document.getElementById(elementId);
        if (!el || el.getAttribute('data-auto-init') !== 'true') return;
        el.removeAttribute('data-auto-init');
        var config = JSON.parse(el.getAttribute('data-init-config') || '{}');
        var mod = window[moduleName];
        if (mod && typeof mod[methodName] === 'function') {
            mod[methodName].apply(mod, config.args || []);
        }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
};

// Project Charts — imported early so its inline code runs before any
// potentially-crashing search/autocomplete modules.
import './modules/v4/project_charts/projects_breakdown.js';

// ─── Application Modules ─────────────────────────────────────────────────────

// Core V4 modules
// import './modules/v4/tags.js'; // REMOVED — migrated to user_tag_select_controller.js
import './modules/v4/algolia_quicksearch.js';
import './modules/v4/notifications_grid.js';
import './modules/v4/tasks_grid.js';

// Workplace & Tenders
import './modules/v4/workplace/utils.js';
import './modules/v4/workplace/widgets.js';
import './modules/v4/workplace/projects.js';
import './modules/v4/workplace/tenders.js';
import './modules/v4/tenders.js';
import './modules/v4/bids.js';
import './modules/v4/bidding_period.js';
import './modules/v4/sidebar_boxes.js';
import './modules/v4/form_of_tenders.js';
import './modules/v4/bid_evaluations_sidebar.js';
import './modules/v4/evaluation_documents.js';
import './modules/v4/recurly_helpers.js';
import './modules/v4/scroll_to_body.js';
import './modules/v4/communicate_decision.js';
import './modules/v4/file_uploads.js';
import './modules/v4/evaluator_assignments.js';
import './modules/v4/company_users.js';
import './modules/v4/company_settings/lists.js';

// Bid Charts
import './modules/v4/bids_charts/bidders_utils.js';
import './modules/v4/bids_charts/bids_summary.js';
import './modules/v4/bids_charts/chart_utils.js';
import './modules/v4/bids_charts/charts.js';
import './modules/v4/bids_charts/common_utils.js';
import './modules/v4/bids_charts/documents_finance.js';
import './modules/v4/bids_charts/documents_summary.js';
import './modules/v4/bids_charts/documents_table.js';
import './modules/v4/bids_charts/documents_utils.js';
import './modules/v4/bids_charts/evaluations_chart.js';
import './modules/v4/bids_charts/report_utils.js';
import './modules/v4/bids_charts/sidebar.js';

// Company Settings
import './modules/v4/company_settings.js';
import './modules/v4/company_templates.js';
import './modules/v4/document_requirements.js';
import './modules/v4/company_settings/bid_evaluation_templates.js';
import './modules/v4/company_settings/bidder_question_templates.js';
import './modules/v4/company_settings/vendor_registrations.js';
import './modules/v4/company_settings/vendor_templates.js';

// Documents
import './modules/v4/document_sets.js';
import './modules/v4/document_issues.js';
import './modules/v4/document_version_reviews.js';
import './modules/v4/document_set_reviews.js';
import './modules/v4/document_sets/documents.js';
import './modules/v4/document_sets/issues.js';
import './modules/v4/document_sets/clarification_requests.js';
import './modules/v4/document_sets/bidder_documents.js';
import './modules/v4/document_sets/document_issues_summary.js';
import './modules/v4/boq_document_version.js';
import './modules/v4/boq_editor/boq_editor.js';
import './modules/v4/boq_editor/boq_tree.js';
import './modules/v4/boq_editor/bid_editor.js';
import './modules/v4/boq_editor/bid_tree.js';
import './modules/v4/pdf_viewer/compare_versions.js';

// Projects & Participants
import './modules/v4/projects.js';
import './modules/v4/participants.js';
import './modules/v4/participants/companies.js';
import './modules/v4/participants/users.js';
import './modules/v4/participants/invites.js';
import './modules/v4/participants/pre_accept_modal.js';
import './modules/v4/invitations.js';

// Search & Browse
import './modules/search/search_utils.js';
import './modules/search/widgets.js';
import './modules/search/load_more_results.js';
import './modules/search/companies_grid_view.js';
import './modules/search/companies_table_view.js';
import './modules/search/company_projects.js';
import './modules/search/public_projects_search.js';
import './modules/search/public_indexes_projects_widgets.js';
import './modules/search/ag_grid_headers_search.js';
import './modules/search/public_projects_index.js';
import './modules/search/map_init.js';
import './modules/search/map_loader.js';
import './modules/search/company_teams_grid.js';
import './modules/search/vendor_registrations.js';

// Products
import './modules/search/products.js';
import './modules/search/product_widgets.js';
import './modules/search/product_approval_widgets.js';
import './modules/search/opportunities.js';
import './modules/search/product_sets.js';
import './modules/search/product_approvals.js';
import './modules/search/product_requests.js';
import './modules/v4/product_approval_reviews.js';

// Dashboards
import './modules/dashboards/intelligence/project_updates.js';
import './modules/dashboards/profile/visitors.js';
import './modules/search/opportunities_tasks.js';
import './modules/search/opportunity_widgets.js';

// Protenders utilities
import './modules/protenders/utilities.js';

// View-mode toggle handler (Grid ↔ Table ↔ List)
import URI from 'urijs';
$(document).on('click', '.view-mode a:not(.grid-list-toggler)', function () {
    var selectedViewMode = $(this).data('view');
    if (!selectedViewMode) return;

    $('.view-mode a').removeClass('active');
    $('.view-mode a[data-view="' + selectedViewMode + '"]').addClass('active');
    $('#view').val(selectedViewMode);

    var queryString = window.location.search.replace(/&?view=([^&]$|[^&]*)/i, '');
    queryString = new URI(queryString).removeSearch(['project_type', 'sortBy']).toString();

    if (!queryString.trim() || queryString.trim() === '?') {
        queryString = '?view=' + selectedViewMode;
    } else {
        queryString = queryString + '&view=' + selectedViewMode;
    }

    if ($('#description').is(':checked')) {
        queryString = queryString + '&description=1';
    }

    var activeViewParam = '';
    if ($('#projects-smart-lists').length > 0 && typeof PublicIndexesProjectsWidgets !== 'undefined') {
        var activeView = $('#projects-smart-lists ul#quick-lists li a.selected').data('type');
        switch (activeView) {
            case 'follower_ids': activeViewParam = '&project_type=liked'; break;
            case 'verified-projects': activeViewParam = '&project_type=verified-projects'; break;
            case 'all-projects': activeViewParam = '&project_type=all-projects'; break;
        }
        window.location = window.location.pathname + queryString + activeViewParam + '&' + PublicIndexesProjectsWidgets.getQuery();
    } else if ($('#companies-smart-lists').length > 0) {
        var companyActiveView = $('#companies-smart-lists ul#quick-lists li a.selected').data('type');
        switch (companyActiveView) {
            case 'follower_ids': activeViewParam = '&company_type=liked'; break;
            case 'all-companies': activeViewParam = '&company_type=all-companies'; break;
        }
        window.location = window.location.pathname + queryString + activeViewParam;
    } else {
        window.location = window.location.pathname + queryString;
    }

    location.hash = '';
    return false;
});

// ─────────────────────────────────────────────────────────────────────────────
// PART 2: protenders_legacy (Foundation + jQuery plugins)
// ─────────────────────────────────────────────────────────────────────────────
import './protenders_legacy.js';

// ─────────────────────────────────────────────────────────────────────────────
// PART 3: Page-specific modules (formerly separate entry points)
// ─────────────────────────────────────────────────────────────────────────────

// map.js — Leaflet map pages (companies, projects, v4/projects)
import './map.js';

// charts.js — Highcharts init (window.Highcharts needed by bids_charts, dashboards, product charts)
import './charts.js';

// reports.js — AG Grid report pages
import './reports.js';

// notifications.js — notifications index
import './notifications.js';

// tasks.js — tasks index
import './tasks.js';

// company_dashboards.js — company dashboard page
import './company_dashboards.js';

// grid_list_toggler.js — shared grid/list toggle component
import './grid_list_toggler.js';

// projects_pdf_js.js — PDF.js viewer for project documents
import './projects_pdf_js.js';

// vendor_registrations.js — vendor registration forms
import './vendor_registrations.js';

// vendor/documents.js — vendor document upload widget
import './vendor/documents.js';

// vendor/registrations/show.js — vendor registration show page
import './vendor/registrations/show.js';

// product_requests — AG Grid grids for product requests
import './product_requests/requirements/developer_grid.js';
import './product_requests/requirements/supplier_grid.js';
import './product_requests/suppliers_info_grid.js';
